import { BlockMapBuilder, ContentBlock, ContentState, EditorState, genKey } from 'draft-js';

const LIST_ITEM_TYPES = ['unordered-list-item', 'ordered-list-item'];

const sameList = (a: ContentBlock, b: ContentBlock) => a.getType() === b.getType() && LIST_ITEM_TYPES.includes(a.getType());

/**
 * A message saved before lines as typed (bug 31856), its gaps written in as empty lines. That spacing put a gap under
 * every line, so when the message switches on its first edit the gaps went with it and the message collapsed (#35478).
 * An empty line after every line but the last gives back the shape, a little taller: a gap was 14px, a line is 20px.
 * Items of one list never had gaps between them, so they stay together.
 *
 * The blocks already there keep their keys, so the caret of the editor being typed in stays put.
 */
export const oldGapsAsEmptyLines = (content: ContentState): ContentState => {
  const blocks: ContentBlock[] = content.getBlocksAsArray();
  const spaced = blocks.flatMap((block, i) => {
    const next = blocks[i + 1];
    if (!next || sameList(block, next)) return [block];

    const emptyLine = new ContentBlock({ key: genKey(), type: 'unstyled', text: '', characterList: block.getCharacterList().clear() });
    return [block, emptyLine];
  });

  return content.merge({ blockMap: BlockMapBuilder.createFromArray(spaced) });
};

/**
 * The editor as it switches to lines as typed: its content, and its history with it. Pushed as an edit, the conversion
 * would be an undo step of its own, and undo would take the empty lines back out and leave the message collapsed in
 * the new spacing. The caret stays where it was, and keeps the focus if it had it.
 */
export const withOldGapsAsEmptyLines = (editorState: EditorState): EditorState => {
  const spaced = EditorState.set(editorState, {
    currentContent: oldGapsAsEmptyLines(editorState.getCurrentContent()),
    undoStack: editorState.getUndoStack().map((content: ContentState) => oldGapsAsEmptyLines(content)),
    redoStack: editorState.getRedoStack().map((content: ContentState) => oldGapsAsEmptyLines(content)),
  });

  const selection = editorState.getSelection();
  return selection.getHasFocus() ? EditorState.forceSelection(spaced, selection) : EditorState.acceptSelection(spaced, selection);
};
